import { useEffect, useState } from "react";
import { advisoryApi, type Goal, type GoalType } from "../advisoryApi";
import { i18n } from "../format";
import { sanitizeIconSvg } from "../svg";
import { SaveBar } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface GoalData {
  goal: Goal;
  goalTypes: { source: "live" | "fallback"; data: GoalType[] };
  platforms: { hasPlatforms: boolean; eligible: { id: string; name?: string; nameI18n?: Record<string, string> }[]; selectedId: string | null } | null;
}

const MAX_DESCRIPTION = 150;

/**
 * Purpose (`goal_input`) — QP's goal-input-widget: the goal type as cards with
 * the tenant's icons (choosing one also sets the name, as in QP), the name, a
 * short description and, when the collection uses them, the platform.
 */
export function GoalStep({ sessionId, addr, readOnly, onSaved }: StepProps) {
  const load = useStepLoad<GoalData>(sessionId, "goal-input", { goalId: addr.goalId });
  const saver = useSaver(onSaved);
  const [goalType, setGoalType] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [base, setBase] = useState({ goalType: null as string | null, name: "", description: "", platformId: "" });

  useEffect(() => {
    const g = load.data?.goal;
    if (!g) return;
    const b = {
      goalType: g.goalType,
      name: g.name ?? "",
      description: g.description ?? "",
      platformId: load.data?.platforms?.selectedId ?? "",
    };
    setGoalType(b.goalType);
    setName(b.name);
    setDescription(b.description);
    setPlatformId(b.platformId);
    setBase(b);
  }, [load.data]);

  if (!addr.goalId) return <p className="muted">This page needs a goal.</p>;
  if (load.loading && !load.data) return <p className="muted">Loading the goal…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;

  const types = [...(load.data?.goalTypes.data ?? [])].sort((a, b) => a.order - b.order);
  const platforms = load.data?.platforms;
  const dirty =
    goalType !== base.goalType || name !== base.name || description !== base.description || platformId !== base.platformId;
  const typeKnown = types.some((t) => t.id === goalType);

  function pickType(t: GoalType) {
    const oldLabel = types.find((x) => x.id === goalType);
    setGoalType(t.id);
    // QP sets the name from the chosen type unless the advisor typed their own.
    if (!name || name === base.name || (oldLabel && name === i18n(oldLabel.labelI18n))) setName(i18n(t.labelI18n));
  }

  async function save() {
    const body: Record<string, unknown> = {};
    if (goalType && goalType !== base.goalType) body.goalType = goalType;
    if (name.trim() && name !== base.name) body.name = name.trim();
    if (description !== base.description) body.description = description.slice(0, MAX_DESCRIPTION);
    if (platformId && platformId !== base.platformId) body.platformId = platformId;
    if (Object.keys(body).length === 0) return;
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "goal-input", { goalId: addr.goalId }, body), {
      goalsChanged: true,
    });
    if (r) setBase({ goalType, name: name.trim() || name, description, platformId });
  }

  return (
    <section className="adv-step-card">
      <div className="field">
        <span>What is the client saving for? *</span>
        <div className="kind-picker adv-goal-picker" role="radiogroup" aria-label="Goal type">
          {types.map((t) => {
            const svg = sanitizeIconSvg(t.iconSvg);
            return (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={goalType === t.id}
                className={goalType === t.id ? "kind-option active" : "kind-option"}
                onClick={() => pickType(t)}
                disabled={readOnly}
              >
                {svg ? (
                  <span className="glyph adv-goal-glyph" dangerouslySetInnerHTML={{ __html: svg }} />
                ) : (
                  <span className="glyph adv-goal-glyph">◎</span>
                )}
                {i18n(t.labelI18n)}
              </button>
            );
          })}
        </div>
        {!typeKnown && goalType && (
          <span className="field-hint">The goal has no type from this list yet — choose one above.</span>
        )}
      </div>

      <div className="field-row">
        <label className="field">
          <span>Goal name *</span>
          <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
        </label>
        {platforms?.hasPlatforms && (
          <label className="field">
            <span>Platform</span>
            <select value={platformId} onChange={(e) => setPlatformId(e.target.value)} disabled={readOnly}>
              <option value="">Choose…</option>
              {platforms.eligible.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name ?? i18n(p.nameI18n)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label className="field">
        <span>Description</span>
        <textarea
          className="adv-textarea"
          rows={2}
          maxLength={MAX_DESCRIPTION}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={readOnly}
          placeholder="In the client's words, what this money is for."
        />
        <span className="field-hint">
          {description.length}/{MAX_DESCRIPTION}
        </span>
      </label>

      <SaveBar onSave={save} saving={saver.saving} dirty={dirty} error={saver.error} readOnly={readOnly} disabled={!goalType} />
    </section>
  );
}
